import React, { useId, useMemo, useRef, useEffect, useState } from 'react'

import clsx from 'clsx'

import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import './ApplyV2SelectMultiple.scss'

interface Option {
    value: string | number
    label?: string | null
}

interface Props {
    value?: (string | number)[]
    label?: string
    onChange?: (value: (string | number)[]) => void
    onFocus?: React.FocusEventHandler<HTMLDivElement>
    onBlur?: React.FocusEventHandler<HTMLDivElement>
    onInput?: (value: string) => void
    isDisabled?: boolean
    className?: string
    id?: string | number
    name?: string
    isLoading?: boolean
    errorMessage?: string | null | boolean | React.ReactNode
    children?: React.ReactNode
    isRequired?: boolean
    placeholder?: string
    autoFocus?: boolean
    isShowErrorMessage?: boolean
    description?: React.ReactNode
    options: Option[]
    isShowClearButton?: boolean
}

const baseClass = 'ApplyV2SelectMultiple'

function ApplyV2SelectMultiple({
    value = [],
    label,
    onChange,
    onFocus,
    onBlur,
    onInput,
    isDisabled = false,
    className = '',
    id,
    name,
    isLoading = false,
    errorMessage,
    children,
    isRequired = false,
    placeholder = '',
    autoFocus,
    isShowErrorMessage = true,
    description,
    options,
    isShowClearButton = false,
}: Props) {
    const defaultId = useId()
    const { getTitle } = useApplyV2NavTranslations()
    const [isOpen, setIsOpen] = React.useState(false)
    const [searchValue, setSearchValue] = React.useState('')
    const [isInputFocused, setIsInputFocused] = React.useState(false)
    const [focusedOptionIndex, setFocusedOptionIndex] = React.useState(-1)
    const [isKeyboardNavigation, setIsKeyboardNavigation] = React.useState(false)
    const [menuPosition, setMenuPosition] = React.useState<'bottom' | 'top'>('bottom')
    const [isMouseInMenu, setIsMouseInMenu] = React.useState(false)
    const selectRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const optionRefs = useRef<(HTMLDivElement | null)[]>([])

    const arrayValue = Array.isArray(value) ? value : []

    const optionsWithLabels = useMemo(
        () =>
            (options ?? []).map((o) => ({
                value: o.value,
                label: o.label ?? o.value.toString(),
            })),
        [options]
    )

    const isOptionSelected = (optionValue: string | number) => {
        return arrayValue.includes(optionValue)
    }

    const selectedOptions = useMemo(() => {
        return optionsWithLabels.filter((option) => arrayValue.includes(option.value))
    }, [optionsWithLabels, arrayValue])

    const filteredOptions = useMemo(() => {
        if (!searchValue) return optionsWithLabels

        return optionsWithLabels.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()))
    }, [optionsWithLabels, searchValue])

    const setCursorToEnd = () => {
        if (inputRef.current) {
            const length = inputRef.current.value.length
            inputRef.current.setSelectionRange(length, length)
        }
    }

    const calculateMenuPosition = () => {
        if (!selectRef.current) return 'bottom'

        const selectRect = selectRef.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - selectRect.bottom
        const spaceAbove = selectRect.top

        const estimatedMenuHeight = Math.min(filteredOptions.length * 44 + 16, 300)

        if (spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow) {
            return 'top'
        }

        return 'bottom'
    }

    const updateMenuPosition = () => {
        if (isOpen) {
            setMenuPosition(calculateMenuPosition())
        }
    }

    useEffect(() => {
        if (isOpen) {
            updateMenuPosition()
        }
    }, [isOpen, filteredOptions.length])

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('scroll', updateMenuPosition, true)
            window.addEventListener('resize', updateMenuPosition)

            return () => {
                window.removeEventListener('scroll', updateMenuPosition, true)
                window.removeEventListener('resize', updateMenuPosition)
            }
        }
    }, [isOpen])

    const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
        e.stopPropagation()
        if (!isDisabled && !isLoading) {
            if (!isOpen) {
                setIsOpen(true)
            }
            setTimeout(() => {
                inputRef.current?.focus()
                setCursorToEnd()
            }, 0)
        }
    }

    const handleSelectClick = () => {
        if (!isDisabled && !isLoading && !isInputFocused) {
            setIsOpen(true)
            setTimeout(() => {
                inputRef.current?.focus()
                setCursorToEnd()
            }, 0)
        }
    }

    const handleContainerFocus = (e: React.FocusEvent<HTMLDivElement>) => {
        setIsInputFocused(true)
        if (!isDisabled) {
            setIsOpen(true)
        }
        onFocus?.(e)

        setTimeout(() => {
            inputRef.current?.focus()
            setCursorToEnd()
        }, 0)
    }

    const handleContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        setIsInputFocused(false)

        setTimeout(() => {
            if (isMouseInMenu) {
                return
            }

            const relatedTarget = e.relatedTarget as Node | null

            if (!selectRef.current || !relatedTarget || !selectRef.current.contains(relatedTarget)) {
                setIsOpen(false)
                setSearchValue('')
                setFocusedOptionIndex(-1)
                setIsKeyboardNavigation(false)
                onBlur?.(e)
            }
        }, 150)
    }

    const handleOptionDeleteClick = (optionValue: string | number) => {
        const newValue = arrayValue.filter((val) => val !== optionValue)
        onChange?.(newValue)
    }

    const handleOptionClick = (optionValue: string | number) => {
        const isSelected = isOptionSelected(optionValue)
        let newValue: (string | number)[]

        if (isSelected) {
            newValue = arrayValue.filter((val) => val !== optionValue)
        } else {
            newValue = [...arrayValue, optionValue]
        }

        onChange?.(newValue)
        setSearchValue('')

        setTimeout(() => {
            inputRef.current?.focus()
            setCursorToEnd()
        }, 0)
    }

    const handleClearAll = (e: React.MouseEvent) => {
        e.stopPropagation()
        onChange?.([])
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        setSearchValue(newValue)
        onInput?.(newValue)

        setFocusedOptionIndex(-1)
        setIsKeyboardNavigation(false)

        if (!isOpen) {
            setIsOpen(true)
        }
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setIsKeyboardNavigation(true)
                if (!isOpen) {
                    setIsOpen(true)
                    setFocusedOptionIndex(0)
                } else {
                    const nextIndex = focusedOptionIndex >= filteredOptions.length - 1 ? 0 : focusedOptionIndex + 1
                    setFocusedOptionIndex(nextIndex)
                }
                break

            case 'ArrowUp':
                e.preventDefault()
                setIsKeyboardNavigation(true)
                if (!isOpen) {
                    setIsOpen(true)
                    setFocusedOptionIndex(filteredOptions.length - 1)
                } else {
                    const prevIndex = focusedOptionIndex <= 0 ? filteredOptions.length - 1 : focusedOptionIndex - 1
                    setFocusedOptionIndex(prevIndex)
                }
                break

            case 'Enter':
                e.preventDefault()
                setIsKeyboardNavigation(true)
                if (focusedOptionIndex >= 0 && focusedOptionIndex < filteredOptions.length) {
                    handleOptionClick(filteredOptions[focusedOptionIndex].value)
                }
                break

            case 'Backspace':
                if (!searchValue && arrayValue.length > 0) {
                    e.preventDefault()
                    const newValue = arrayValue.slice(0, -1)
                    onChange?.(newValue)
                }
                break

            case 'Escape':
                setIsOpen(false)
                setSearchValue('')
                setFocusedOptionIndex(-1)
                setIsKeyboardNavigation(false)
                inputRef.current?.blur()
                break

            case 'Tab':
                setIsOpen(false)
                setSearchValue('')
                setFocusedOptionIndex(-1)
                setIsKeyboardNavigation(false)
                break

            default:
                setIsKeyboardNavigation(false)
                break
        }
    }

    const handleOptionMouseEnter = (index: number) => {
        if (!isKeyboardNavigation) {
            setFocusedOptionIndex(index)
        }
    }

    const handleMenuMouseMove = () => {
        setIsKeyboardNavigation(false)
    }

    const handleMenuMouseEnter = () => {
        setIsMouseInMenu(true)
    }

    const handleMenuMouseLeave = () => {
        setIsMouseInMenu(false)
    }

    useEffect(() => {
        if (focusedOptionIndex >= 0 && menuRef.current && optionRefs.current[focusedOptionIndex]) {
            optionRefs.current[focusedOptionIndex]?.scrollIntoView({
                block: 'nearest',
            })
        }
    }, [focusedOptionIndex])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false)
                setSearchValue('')
                setFocusedOptionIndex(-1)
                setIsKeyboardNavigation(false)
                setIsMouseInMenu(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        if (!isOpen) {
            setSearchValue('')
            setFocusedOptionIndex(-1)
            setIsKeyboardNavigation(false)
            setIsMouseInMenu(false)
        }
    }, [isOpen])

    useEffect(() => {
        optionRefs.current = optionRefs.current.slice(0, filteredOptions.length)
    }, [filteredOptions])

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessage,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
                [`${baseClass}--open`]: isOpen,
            })}
            ref={selectRef}
            data-error={!!errorMessage ? 'true' : 'false'}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={id ? id.toString() : defaultId}>
                    {label}
                    {isRequired && '*'}
                </label>
            )}
            <div
                className={clsx(`${baseClass}__input-wrapper`, {
                    [`${baseClass}__input-wrapper--disabled`]: isDisabled,
                    [`${baseClass}__input-wrapper--loading`]: isLoading,
                    [`${baseClass}__input-wrapper--error`]: !!errorMessage,
                    [`${baseClass}__input-wrapper--open`]: isOpen,
                })}
            >
                <div
                    className={clsx(`${baseClass}__select`, {
                        [`${baseClass}__select--error`]: !!errorMessage,
                        [`${baseClass}__select--loading`]: isLoading,
                        [`${baseClass}__select--open`]: isOpen,
                    })}
                    onClick={handleSelectClick}
                    onFocus={handleContainerFocus}
                    onBlur={handleContainerBlur}
                    tabIndex={-1}
                >
                    <div className={`${baseClass}__selected-values`}>
                        {selectedOptions.map((option, index) => (
                            <div key={option.value} className={`${baseClass}__selected-value`}>
                                <span className={`${baseClass}__selected-value-label`}>{option.label}</span>
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleOptionDeleteClick(option.value)
                                    }}
                                    className={`${baseClass}__selected-value-close`}
                                >
                                    <svg
                                        width="8"
                                        height="8"
                                        viewBox="0 0 8 8"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7.26496 0.122039C7.42768 0.284757 7.42768 0.548576 7.26496 0.711294L4.28275 3.69352L7.37796 6.7887C7.54068 6.95142 7.54068 7.21524 7.37796 7.37796C7.21524 7.54068 6.95143 7.54068 6.78871 7.37796L3.69349 4.28277L0.711294 7.26496C0.548576 7.42768 0.284757 7.42768 0.122039 7.26496C-0.0406797 7.10225 -0.0406795 6.83843 0.122039 6.67571L3.10424 3.69352L0.235033 0.82429C0.0723153 0.661571 0.0723164 0.397752 0.235036 0.235034C0.397755 0.0723162 0.661573 0.0723173 0.824291 0.235036L3.69349 3.10426L6.67571 0.122039C6.83843 -0.0406795 7.10225 -0.0406797 7.26496 0.122039Z"
                                            fill="#58585B"
                                        />
                                    </svg>
                                </span>
                            </div>
                        ))}
                    </div>
                    <input
                        ref={inputRef}
                        type="text"
                        id={id ? id.toString() : defaultId}
                        name={name}
                        value={searchValue}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        onClick={handleInputClick}
                        onFocus={handleContainerFocus}
                        disabled={isDisabled}
                        placeholder={arrayValue.length === 0 ? placeholder : ''}
                        autoFocus={autoFocus}
                        autoComplete="off"
                        className={clsx(`${baseClass}__input`, {
                            [`${baseClass}__input--error`]: !!errorMessage,
                            [`${baseClass}__input--loading`]: isLoading,
                        })}
                        tabIndex={0}
                    />
                    {arrayValue.length > 0 && isShowClearButton && (
                        <button
                            type="button"
                            className={`${baseClass}__clear-all`}
                            onClick={handleClearAll}
                            disabled={isDisabled}
                            tabIndex={-1}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                    d="M12 4L4 12M4 4L12 12"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    )}
                    <div
                        className={clsx(`${baseClass}__arrow`, {
                            [`${baseClass}__arrow--open`]: isOpen,
                        })}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M11.7579 5.90901C11.5235 5.6747 11.1436 5.6747 10.9093 5.90901L8.00026 8.81808L5.09119 5.90901C4.85688 5.6747 4.47698 5.6747 4.24266 5.90901C4.00835 6.14333 4.00835 6.52323 4.24266 6.75754L7.576 10.0909C7.81031 10.3252 8.19021 10.3252 8.42452 10.0909L11.7579 6.75754C11.9922 6.52323 11.9922 6.14333 11.7579 5.90901Z"
                                fill="#19191A"
                            />
                        </svg>
                    </div>
                </div>

                {isOpen && (
                    <div
                        className={clsx(`${baseClass}__menu`, {
                            [`${baseClass}__menu--keyboard-navigation`]: isKeyboardNavigation,
                            [`${baseClass}__menu--top`]: menuPosition === 'top',
                            [`${baseClass}__menu--bottom`]: menuPosition === 'bottom',
                        })}
                        ref={menuRef}
                        onMouseMove={handleMenuMouseMove}
                        onMouseEnter={handleMenuMouseEnter}
                        onMouseLeave={handleMenuMouseLeave}
                    >
                        {filteredOptions.length === 0 ? (
                            <div className={`${baseClass}__no-options`}>{getTitle('5815', 'No results found')}</div>
                        ) : (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={option.value}
                                    ref={(el) => (optionRefs.current[index] = el)}
                                    className={clsx(`${baseClass}__option`, {
                                        [`${baseClass}__option--selected`]: isOptionSelected(option.value),
                                        [`${baseClass}__option--focused`]: index === focusedOptionIndex,
                                        [`${baseClass}__option--disabled`]: isDisabled,
                                    })}
                                    onClick={() => handleOptionClick(option.value)}
                                    onMouseEnter={() => handleOptionMouseEnter(index)}
                                >
                                    <div className={`${baseClass}__option-checkbox`}>
                                        <input
                                            type="checkbox"
                                            checked={isOptionSelected(option.value)}
                                            readOnly
                                            className={`${baseClass}__checkbox-input`}
                                            tabIndex={-1}
                                        />
                                        <span className={`${baseClass}__checkbox-custom`}>
                                            {isOptionSelected(option.value) && (
                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                    <path
                                                        d="M10 3L4.5 8.5L2 6"
                                                        stroke="white"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            )}
                                        </span>
                                    </div>
                                    <div className={`${baseClass}__option-label`}>{option.label}</div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {description && <div className={`${baseClass}__description`}>{description}</div>}
            {errorMessage && isShowErrorMessage && (
                <div className={`${baseClass}__error`}>
                    <div className={`${baseClass}__error-icon`}>
                        <WarningErrorIcon />
                    </div>
                    <div className={`${baseClass}__error-text`}>{errorMessage}</div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2SelectMultiple
